/**
 * The ship model stream (M10 P1b1, hull tiers spec §6.5): one loader for ship models, with per-system sets. Boot's
 * models come in as `preloaded`; the rest load when something asks for them (the traffic layer's manifest and delta
 * spawns today; stand-ins, the binder and the "Prepping ship" hold in later P1 cards). It wraps the Reach
 * `ModelCache`, which dedupes loads and forgets a failed id so a later `ensure` retries it.
 *
 * - `ensure(ids)` resolves once every id is in (preloaded ids and ids the stream does not handle are no-ops) and
 *   rejects with the cache's `AssetLoadError` for a failed one. Each failure is logged once, here.
 * - `request(ids)` is `ensure` for callers that don't wait (the rejection is already logged, so it is swallowed:
 *   an unhandled rejection would stop the game loop).
 * - `generation` goes up by one per model that lands; `onLoaded` listeners hear each landed id once.
 * - `assetsOfDefs(defIds, defs)` maps ship def ids to their unique asset ids (unknown defs are skipped).
 * - M10 P1b2: `getOrStandIn(id)` is the real model, or for a streamed id not loaded yet its band-sized stand-in
 *   (`standIn.ts`), and handing out a stand-in also starts its load (`request`, deduped). So every ship that shows a
 *   stand-in loads its model, whoever spawned it: delta spawns, mission and story ships, raiders, `spawnLawful`
 *   (plan review M7). `colliderOf(id)` gives the stand-in's box collider for a streamed id not loaded yet, without
 *   starting a load (boot reads it for every def). `isStandIn` (`standIn.ts`) tells the two apart.
 *
 * Off by default (`ship-stream.json enabled`): boot still loads every ship, so everything is preloaded and nothing
 * loads here. Never per frame: every call is message rate or rarer.
 */
import { z } from 'zod'
import { logger } from '../core/logger'
import type { AssetsFile, ModelEntry } from '../data/assetSchema'
import shipStreamJson from '../data/ship-stream.json'
import type { ColliderData, LoadedModel } from './loader'
import { createModelCache, type ModelLoadSource } from './modelCache'
import { standInModel } from './standIn'

const assetIdSchema = z.string().regex(/^ship\.[a-z0-9_]+$/)

export const shipStreamConfigSchema = z.object({
  comment: z.string().optional(),
  /** Streaming on (P1b3). Off: boot loads every ship model, as before M10. */
  enabled: z.boolean(),
  /** Ship models boot still loads while streaming is on (outside the M1 sandbox). */
  bootShips: z.array(assetIdSchema),
  /** Ship models boot never loads, in any mode, flag or not. */
  streamedShips: z.array(assetIdSchema),
  /** [P] Seconds the P1b3 "Prepping ship" hold waits before it offers Retry. */
  prepTimeoutS: z.number().positive(),
})

export type ShipStreamConfig = z.infer<typeof shipStreamConfigSchema>

export function loadShipStreamConfig(): ShipStreamConfig {
  return shipStreamConfigSchema.parse(shipStreamJson)
}

/** The fields of a ship def the stream reads. */
export interface ShipDefAsset {
  readonly id: string
  readonly assetId: string
}

export interface ShipStream {
  ensure(assetIds: readonly string[]): Promise<void>
  /** `ensure` without waiting; failures are logged by the stream and otherwise ignored. */
  request(assetIds: readonly string[]): void
  get(id: string): LoadedModel | undefined
  /**
   * M10 P1b2: the loaded model, else (for a streamed id with a manifest entry) its stand-in, whose hand-out starts
   * the load; undefined for an id the stream neither holds nor streams. Rare paths only (a view acquire or rebuild).
   */
  getOrStandIn(id: string): LoadedModel | undefined
  /** The loaded model's collider; for a streamed id not loaded yet, the stand-in's box (no load is started). */
  colliderOf(id: string): ColliderData | null
  /** Bumped once per model that lands. */
  readonly generation: number
  /** `cb` hears every landed asset id once. Returns the unsubscribe. */
  onLoaded(cb: (assetId: string) => void): () => void
  /** The unique asset ids of `defIds` (in first-seen order); unknown def ids are skipped. */
  assetsOfDefs(defIds: Iterable<string>, defs: readonly ShipDefAsset[]): string[]
}

export interface ShipStreamOptions {
  loader: ModelLoadSource
  assets: AssetsFile
  /** Boot's models: served as-is, never reloaded. */
  preloaded: ReadonlyMap<string, LoadedModel>
  /** True for the ids this stream loads (boot skipped them); any other id that isn't preloaded is ignored. */
  isStreamed(id: string): boolean
}

function ignore(): void {}

export function createShipStream(opts: ShipStreamOptions): ShipStream {
  const cache = createModelCache(opts.loader, opts.assets, opts.preloaded)
  const pending = new Map<string, Promise<void>>()
  const listeners = new Set<(assetId: string) => void>()
  const entries = new Map<string, ModelEntry>()
  for (const e of opts.assets.models) entries.set(e.id, e)
  let generation = 0

  /** The stand-in for a streamed id that isn't loaded (undefined otherwise). */
  const standInOf = (id: string): LoadedModel | undefined => {
    if (cache.get(id) !== undefined || !opts.isStreamed(id)) return undefined
    const entry = entries.get(id)
    return entry === undefined ? undefined : standInModel(entry)
  }

  const load = (id: string): Promise<void> => {
    const inFlight = pending.get(id)
    if (inFlight) return inFlight
    const promise = cache.ensure([id]).then(
      () => {
        pending.delete(id)
        generation++
        for (const cb of listeners) cb(id)
      },
      (err: unknown) => {
        pending.delete(id)
        logger.warn(`ship stream: ${id} failed to load (retried on the next ensure)`, err)
        throw err
      },
    )
    pending.set(id, promise)
    return promise
  }

  const stream: ShipStream = {
    async ensure(assetIds: readonly string[]): Promise<void> {
      const waits: Promise<void>[] = []
      for (const id of new Set(assetIds)) {
        if (cache.get(id) !== undefined || !opts.isStreamed(id)) continue
        waits.push(load(id))
      }
      await Promise.all(waits)
    },
    request(assetIds: readonly string[]): void {
      stream.ensure(assetIds).catch(ignore)
    },
    get: (id) => cache.get(id),
    getOrStandIn(id: string): LoadedModel | undefined {
      const real = cache.get(id)
      if (real !== undefined) return real
      const standIn = standInOf(id)
      if (standIn !== undefined) stream.request([id])
      return standIn
    },
    colliderOf: (id) => standInOf(id)?.collider ?? cache.colliderOf(id),
    get generation(): number {
      return generation
    },
    onLoaded(cb: (assetId: string) => void): () => void {
      listeners.add(cb)
      return () => {
        listeners.delete(cb)
      }
    },
    assetsOfDefs(defIds: Iterable<string>, defs: readonly ShipDefAsset[]): string[] {
      const byId = new Map<string, string>()
      for (const d of defs) byId.set(d.id, d.assetId)
      const out = new Set<string>()
      for (const defId of defIds) {
        const assetId = byId.get(defId)
        if (assetId !== undefined) out.add(assetId)
      }
      return [...out]
    },
  }
  return stream
}

declare module '../app/services' {
  interface ServiceMap {
    /** M10 P1b1: the ship model stream (provided by boot once the services exist). */
    ships: ShipStream
  }
}
